import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { BACKEND, getToken } from "../lib/api";
import { useAuth } from "../lib/auth";

const GREETING = "Hello — I'm the Studio Match Assistant. I'll ask a few short questions about you and what you're looking for, then suggest Guild members who might be a good fit. To start: who are you, and what kind of ceramics work do you do?";
const newSession = () => `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function MatchResults({ result, user }) {
  return (
    <div className="space-y-2 rounded-xl border border-celadon/50 bg-celadon-light/60 p-3 text-sm" data-testid="chat-match-results">
      <p className="font-semibold text-ink">Your request is saved for staff review.</p>
      {result.matches.length ? (
        <ul className="space-y-2">
          {result.matches.map((m) => (
            <li key={m.id} className="rounded-lg bg-white p-2.5" data-testid={`chat-match-${m.id}`}>
              <Link to={`/directory/${m.id}`} className="font-medium text-ink underline decoration-ochre">{m.name}</Link>
              <span className="text-[#4a4f59]"> {m.studio_name ? `· ${m.studio_name}` : ""}</span>
              <p className="text-xs text-[#4a4f59]">{m.reason}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[#4a4f59]">No public profiles match closely yet — our staff will search the full membership for you.</p>
      )}
      <p className="text-xs text-[#4a4f59]">A Guild staff member will review your request and make any introduction personally. Nothing is shared automatically.</p>
      {!user && <Link to="/register" className="btn-ochre btn-sm" data-testid="chat-join-link">Join the Guild</Link>}
    </div>
  );
}

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sid, setSid] = useState(() => sessionStorage.getItem("kh_chat") || newSession());
  const endRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem("kh_chat", sid);
  }, [sid]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, result]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    const update = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: fn(c[c.length - 1].text) }; return c; });
    try {
      const headers = { "Content-Type": "application/json" };
      if (getToken()) headers.Authorization = `Bearer ${getToken()}`;
      const res = await fetch(`${BACKEND}/api/match/chat`, { method: "POST", headers, body: JSON.stringify({ session_id: sid, message: text }) });
      if (!res.ok) throw new Error("bad");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === "delta") update((t) => (t + ev.text));
          if (ev.type === "error") update(() => ev.text);
          if (ev.type === "matches") setResult(ev);
        }
      }
    } catch {
      update(() => "Sorry — I couldn't connect just now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const restart = () => { setSid(newSession()); setMsgs([{ role: "assistant", text: GREETING }]); setResult(null); };

  if (!open)
    return (
      <button onClick={() => setOpen(true)} data-testid="chat-open-btn" className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-paper shadow-lg transition-transform hover:-translate-y-0.5">
        <Sparkles className="h-4 w-4 text-ochre" aria-hidden="true" /> Studio Match
      </button>
    );

  return (
    <section aria-label="Studio Match Assistant" data-testid="chat-panel" className="fixed inset-x-3 bottom-3 z-50 flex max-h-[80vh] flex-col overflow-hidden rounded-2xl border border-line bg-paper shadow-2xl sm:inset-x-auto sm:right-5 sm:w-[400px]">
      <div className="flex items-center justify-between bg-ink px-4 py-3 text-paper">
        <div>
          <p className="font-serif text-lg text-paper">Studio Match Assistant</p>
          <p className="text-xs text-paper/75">Staff review every introduction</p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={restart} className="rounded-full px-2 py-1 text-xs text-paper/80 hover:bg-white/10" data-testid="chat-restart-btn">Restart</button>
          <button onClick={() => setOpen(false)} aria-label="Close assistant" className="rounded-full p-1.5 hover:bg-white/10" data-testid="chat-close-btn"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4" aria-live="polite" data-testid="chat-messages">
        {msgs.map((m, i) => (
          <div key={i} className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "ml-auto bg-ink text-paper" : "bg-white text-charcoal border border-line"}`} data-testid={`chat-msg-${m.role}`}>
            {m.text.split("<PROFILE>")[0] || <Loader2 className="h-4 w-4 animate-spin" aria-label="Assistant is typing" />}
          </div>
        ))}
        {result && <MatchResults result={result} user={user} />}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-line bg-white p-3">
        <label htmlFor="chat-input" className="sr-only">Your message</label>
        <input id="chat-input" value={input} onChange={(e) => setInput(e.target.value)} disabled={busy || !!result} placeholder={result ? "Request saved — restart to begin again" : "Type your answer…"} className="kh-input" data-testid="chat-input" />
        <button type="submit" disabled={busy || !input.trim() || !!result} className="btn-primary px-3" aria-label="Send" data-testid="chat-send-btn"><Send className="h-4 w-4" /></button>
      </form>
    </section>
  );
}
